/**
 * The icon set.
 *
 * ---------------------------------------------------------------------------
 * WHY LUCIDE, AND WHY BEHIND A MAP
 * ---------------------------------------------------------------------------
 * Lucide is MIT, tree-shakeable per icon (~0.4 kB of JS each, no font, no
 * sprite sheet, no extra request) and — the reason it was chosen over drawing
 * them by hand — it is a *system*: 24×24 grid, 2 px nominal stroke, the same
 * optical weight and the same terminal shapes across every glyph. The previous
 * shell used Unicode marks (◎ ▤ ⇄ ⛁ ⚠ ☰), which render at a different weight
 * and baseline on every platform and turn into colour emoji on iOS.
 *
 * Every icon goes through the `ICONS` map rather than being imported at its
 * point of use. Three reasons:
 *   1. It is the inventory. Adding a glyph is a deliberate act, visible in one
 *      diff, so the set stays small and coherent instead of accreting.
 *   2. The stroke weight and the `aria-hidden` are set once. Icons in this
 *      application are ALWAYS decorative — every one of them sits beside a real
 *      word, because an icon-only status is a status you have to have learned.
 *   3. The bundler only ever sees the ~30 modules named here.
 *
 * Sizes: 14 inside a chip, 16 in a row, 18–20 in navigation. Below 14 a 2 px
 * stroke on a 24 px grid stops resolving on a phone, so nothing is smaller.
 */

import {
  ArrowLeft,
  ArrowRight,
  Ban,
  Banknote,
  BookOpen,
  Check,
  ChevronRight,
  ChevronsDown,
  ChevronsUp,
  CircleAlert,
  CircleCheck,
  CircleDashed,
  CircleX,
  ClipboardList,
  Clock,
  Database,
  Gauge,
  Globe,
  HardDrive,
  Layers,
  ListChecks,
  LockOpen,
  Minus,
  Radio,
  RotateCw,
  ScanLine,
  Search,
  Server,
  ShieldAlert,
  TriangleAlert,
  Waypoints,
  X,
  type LucideIcon,
} from 'lucide-react';

export const ICONS = {
  /* navigation */
  overview: Gauge,
  servers: Server,
  services: Globe,
  databases: Database,
  errors: TriangleAlert,
  findings: ClipboardList,

  /* the four-state vocabulary (see lib/signal.ts#iconFor) */
  ok: Check,
  okRing: CircleCheck,
  empty: Minus,
  unproven: CircleDashed,
  blocked: Ban,
  error: CircleX,
  warn: CircleAlert,
  aboveTopBucket: ChevronsUp,
  belowRequestFloor: ChevronsDown,

  /* actions and affordances */
  reload: RotateCw,
  key: BookOpen,
  close: X,
  back: ArrowLeft,
  forward: ArrowRight,
  chevron: ChevronRight,
  search: Search,

  /* nouns the pages talk about */
  clock: Clock,
  cost: Banknote,
  deadman: Radio,
  storage: HardDrive,
  placement: Waypoints,
  snapshot: Layers,
  checks: ListChecks,
  coverage: ScanLine,
  insecure: LockOpen,
  security: ShieldAlert,
} as const;

export type IconName = keyof typeof ICONS;

/**
 * A decorative icon.
 *
 * `aria-hidden` is not optional and not a prop: there is no call site in this
 * application where an icon is the only carrier of meaning, and making that
 * unexpressible is cheaper than auditing it.
 */
export function Icon({
  name,
  size = 16,
  className,
  strokeWidth = 1.75,
}: {
  name: IconName;
  size?: number;
  className?: string;
  strokeWidth?: number;
}): React.ReactElement {
  const Glyph: LucideIcon = ICONS[name];
  return (
    <Glyph
      size={size}
      strokeWidth={strokeWidth}
      aria-hidden="true"
      focusable="false"
      className={`shrink-0 ${className ?? ''}`}
    />
  );
}
